import React, { useRef, useState } from 'react';
import { Animated, Platform, StyleSheet, TouchableOpacity } from 'react-native';

import useActivityStepsCarouselAnimations from './activity-steps-carousel-animations';
import { useActivityContext } from '../../../hooks/use-activity.context';
import type { TabActivityStepInterface } from '../../../interfaces/tab-activity';
import Carousel from '../../carousel/carousel';
import type TabActivityCard from '../lesson/tab-activity/tab-activity-card';
import type { CarouselDimensions } from '../lesson/tab-activity/use-tab-activity-carousel-dimensions';

interface ActivityStepsCarouselProps {
  steps: TabActivityStepInterface[];
  dimensions: CarouselDimensions;
  ItemToRender: typeof TabActivityCard;
  onLastItemReached: () => void;
  onCurrentIndexChanged?: (index: number) => void;
}

const outlineStyle = Platform.OS === 'web' ? { outlineStyle: 'none' } : null;

const ActivityStepsCarousel: React.FC<ActivityStepsCarouselProps> = ({
  steps,
  dimensions,
  ItemToRender,
  onLastItemReached,
  onCurrentIndexChanged,
}) => {
  const scrollX = useRef(new Animated.Value(0)).current;
  const { isStepComplete } = useActivityContext();
  const { cardGap, cardWidth, cardHeight } = dimensions;
  const [currentIndex, setCurrentIndex] = useState(0);
  const getAnimations = useActivityStepsCarouselAnimations(
    cardWidth,
    cardGap,
    scrollX
  );

  const onCurrentIndexChange = (index: number) => {
    setCurrentIndex(index);
    onCurrentIndexChanged?.(index);
  };

  const renderItem = ({ item, index }: { item: any; index: number }) => {
    const { transform, opacity } = getAnimations(index, isStepComplete(index));

    return (
      <Animated.View
        style={{
          ...styles.item,
          ...outlineStyle,
          width: cardWidth,
          opacity,
          transform,
        }}
      >
        <TouchableOpacity
          disabled={currentIndex === index}
          onPress={() => setCurrentIndex(index)}
        >
          <ItemToRender
            item={item}
            cardWidth={cardWidth}
            cardHeight={cardHeight}
            index={index}
            active={currentIndex === index}
          />
        </TouchableOpacity>
      </Animated.View>
    );
  };

  return (
    <Carousel
      items={steps}
      renderFn={renderItem}
      showPrevNextButtons={Platform.OS === 'web'}
      scrollXRef={scrollX}
      dimensions={dimensions}
      onLastItemReached={onLastItemReached}
      currentIndex={currentIndex}
      setCurrentIndex={onCurrentIndexChange}
    />
  );
};

export default ActivityStepsCarousel;

const styles = StyleSheet.create({
  item: {
    flex: 1,
    paddingVertical: 5,
  },
});
